import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Server, Phone, Mail, MessageCircle, Search, ShoppingCart, Plus, KeyRound, Copy, RefreshCw, Shield } from 'lucide-react';
import FieldHelp from '@/components/help/FieldHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import SesInfraSection from '@/components/admin/SesInfraSection';
import RemitenteAvisosSection from '@/components/admin/RemitenteAvisosSection';

const FIELD_HELP = {
  account_sid: 'El SID de tu cuenta de Twilio (empieza con "AC..."), en Console → Account Info. Esto identifica la cuenta, no un número — el número se agrega en el paso 2, más abajo.',
  auth_token: 'El Auth Token de tu cuenta de Twilio, junto al Account SID en Console → Account Info.',
  app_key: 'La App Key de tu app de Gupshup (WABA ya aprobado por Meta).',
  api_url: 'La URL base de la API de Gupshup para esta app.',
  smtp_host: 'El servidor SMTP de tu proveedor de correo (ej. smtp.sendgrid.net).',
  smtp_user: 'Usuario/API key para autenticarte con el servidor SMTP.',
  smtp_password: 'Contraseña o API key secreta del servidor SMTP.',
  from_email: 'La dirección "de" que verán los tenants que usan esta infraestructura.',
  turnstile_site: 'Clave de sitio (site key) de Cloudflare Turnstile. Es pública: se usa en el widget del login.',
  turnstile_secret: 'Clave secreta de Turnstile. Nunca se muestra en el frontend; el backend la usa para validar el token.',
  turnstile_hostnames: 'Hostname(s) permitidos que Cloudflare debe devolver (separados por coma). Debe incluir app.cavernicolapp.com.',
  r2_account: 'Account ID de Cloudflare (32 hex). Se usa para validar el endpoint S3 de R2; no se muestra completo en la API.',
  r2_access: 'Access Key ID de las credenciales S3 de R2. Write-only: déjalo vacío para conservar el valor guardado.',
  r2_secret: 'Secret Access Key de R2. Nunca se devuelve al navegador. Déjalo vacío para no cambiarlo.',
  r2_bucket: 'Nombre del bucket privado (staging o prod).',
  r2_endpoint: 'Endpoint S3 de R2 (https://<account_id>.r2.cloudflarestorage.com). Si lo dejas vacío se deriva del Account ID.',
  r2_cors: 'Orígenes CORS esperados (referencia). Debes configurarlos en el dashboard de Cloudflare R2; la app no los aplica al bucket.',
  r2_enabled: 'Habilita R2 a nivel plataforma. Requiere además STORAGE_R2_ENABLED=true en el servidor. No activa Knowledge/Social automáticamente.',
};

const TWILIO_FLOW_HELP = 'Este proveedor funciona en 2 pasos: (1) guarda aquí la cuenta (Account SID + Auth Token) — es la cuenta, no un número; (2) usa "Buscar números disponibles" más abajo para que el sistema te muestre números nuevos y los compre por ti. No hay que escribir ningún número en este formulario.';

// Brand color blocks (UX only — does not change credentials/API).
const BRAND = {
  twilio: { name: 'Twilio', card: 'bg-[#FFF5F6] border-[#F22F46]/35', bar: 'bg-[#F22F46]', title: 'text-[#B91C1C]' },
  gupshup: { name: 'Gupshup', card: 'bg-[#F5F0FA] border-[#6B3FA0]/35', bar: 'bg-[#6B3FA0]', title: 'text-[#5B21B6]' },
  google: { name: 'Google', card: 'bg-gradient-to-br from-yellow-50 to-green-50 border-yellow-400/50', bar: 'bg-gradient-to-r from-yellow-400 to-green-500', title: 'text-green-800' },
  microsoft: { name: 'Microsoft', card: 'bg-[#F3F9FD] border-[#0078D4]/35', bar: 'bg-[#0078D4]', title: 'text-[#0078D4]' },
  meta: { name: 'Meta / Facebook', card: 'bg-[#EFF6FF] border-[#1877F2]/35', bar: 'bg-[#1877F2]', title: 'text-[#1D4ED8]' },
  instagram: { name: 'Instagram', card: 'bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 border-pink-300', bar: 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400', title: 'text-pink-700' },
  linkedin: { name: 'LinkedIn', card: 'bg-white border-slate-300 shadow-sm', bar: 'bg-slate-300', title: 'text-[#0A66C2]' },
  tiktok: { name: 'TikTok', card: 'bg-zinc-950 border-zinc-700 text-white', bar: 'bg-[#FE2C55]', title: 'text-white', dark: true },
  email: { name: 'Email', card: 'bg-slate-50 border-slate-200', bar: 'bg-slate-400', title: 'text-slate-800' },
  cloudflare: { name: 'Cloudflare', card: 'bg-orange-50 border-orange-300/50', bar: 'bg-orange-500', title: 'text-orange-800' },
};

const PROVIDERS = [
  { key: 'twilio_voice', label: 'Twilio Voz', icon: Phone, channel: 'voice', fields: ['account_sid', 'auth_token'], autoSearch: true, brand: 'twilio' },
  { key: 'twilio_whatsapp', label: 'Twilio WhatsApp', icon: MessageCircle, channel: 'whatsapp', fields: ['account_sid', 'auth_token'], autoSearch: true, brand: 'twilio' },
  { key: 'gupshup', label: 'Gupshup (WhatsApp)', icon: MessageCircle, channel: 'whatsapp', fields: ['app_key', 'api_url'], autoSearch: false, brand: 'gupshup', showManualNumber: true },
  { key: 'email', label: 'Email (SMTP)', icon: Mail, channel: 'email', fields: ['smtp_host', 'smtp_user', 'smtp_password', 'from_email'], autoSearch: false, brand: 'email' },
];

// Sprint 57 (4/N) — apps OAuth de Google/Meta: a diferencia de los
// proveedores de arriba, aquí no hay "buscar números" — solo client_id y
// client_secret que el Súper Admin obtiene del panel de desarrolladores de
// cada proveedor. El redirect_uri lo calcula el backend (nunca se teclea
// a mano) para que copiarlo y pegarlo en Google/Meta sea siempre exacto.
const OAUTH_APPS = [
  { key: 'google_oauth', label: 'Google (Calendar, Sheets, etc.)', icon: KeyRound, brand: 'google', group: 'google',
    helpUrl: 'https://console.cloud.google.com/apis/credentials',
    credentialHelp: 'Client ID y Client Secret salen de Google Cloud Console → Credenciales → OAuth 2.0. Preparado para Calendar y, en el futuro, Google Sheets.' },
  { key: 'outlook_oauth', label: 'Outlook / Microsoft 365 (Calendar)', icon: KeyRound, brand: 'microsoft', group: 'microsoft',
    helpUrl: 'https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
    credentialHelp: 'En Microsoft Entra ID (Azure) → Registros de aplicaciones → tu app → Certificados y secretos (Client secret) + Información general (Application/Client ID). Redirect URI tipo Web: el que muestra esta tarjeta (…/api/tenant/oauth/outlook/callback). Permisos delegados Graph: Calendars.ReadWrite, User.Read, offline_access, openid, profile, email. IMPORTANTE: en Client Secret pega el VALUE (valor) del secreto, NO el Secret ID (GUID). El Value solo se muestra una vez al crearlo; si no lo guardaste, crea un secreto nuevo. Cuentas: multitenant + personales si quieres Outlook.com.' },
  { key: 'meta_oauth', label: 'Meta (Facebook)', icon: KeyRound, brand: 'meta', group: 'meta',
    helpUrl: 'https://developers.facebook.com/apps',
    credentialHelp: 'ID de la app y Secreto de la app (Facebook Login, Fan Page, Messenger, Meta Ads). Instagram va en su propio bloque rosa.',
    hasWebhook: true },
  { key: 'instagram_oauth', label: 'Instagram (Instagram Login)', icon: KeyRound, brand: 'instagram', group: 'instagram',
    helpUrl: 'https://developers.facebook.com/apps',
    credentialHelp: 'ID/Secreto de Instagram Login — distintos al de Facebook aunque vivan en la misma App de Meta.',
    hasWebhook: false },
  { key: 'tiktok_oauth', label: 'TikTok for Developers (Login + Publicaciones)', icon: KeyRound, brand: 'tiktok', group: 'tiktok',
    helpUrl: 'https://developers.tiktok.com/apps',
    credentialHelp: 'Client Key/Secret de developers.tiktok.com. Login Kit + Content Posting — no anuncios.',
    products: [
      { key: 'login_kit', label: 'Login Kit (inicio de sesión)' },
      { key: 'content_posting_api', label: 'Content Posting API (publicar videos)' },
      { key: 'audited', label: 'App auditada por TikTok' },
    ],
    hasWebhook: false },
  { key: 'tiktok_business_oauth', label: 'TikTok for Business (Ads / Mensajería)', icon: KeyRound, brand: 'tiktok', group: 'tiktok',
    helpUrl: 'https://business-api.tiktok.com/portal',
    credentialHelp: 'App SEPARADA en business-api.tiktok.com — no reutilices credenciales de TikTok Developers.',
    products: [
      { key: 'marketing_api', label: 'Marketing API (anuncios)' },
      { key: 'organic_api', label: 'Organic API' },
      { key: 'business_messaging_api', label: 'Business Messaging API' },
    ],
    hasWebhook: false },
  { key: 'linkedin_oauth', label: 'LinkedIn (Login + Publicaciones)', icon: KeyRound, brand: 'linkedin', group: 'linkedin',
    helpUrl: 'https://www.linkedin.com/developers/apps',
    credentialHelp: 'Client ID/Secret en linkedin.com/developers → Auth. Login + publicación personal.',
    products: [
      { key: 'oidc_login', label: 'Sign In with LinkedIn (OpenID Connect)' },
      { key: 'w_member_social', label: 'Publicación personal (w_member_social)' },
      { key: 'messages_api', label: 'Communications/Messages API' },
    ],
    hasWebhook: false },
  { key: 'linkedin_ads_oauth', label: 'LinkedIn Ads', icon: KeyRound, brand: 'linkedin', group: 'linkedin',
    helpUrl: 'https://www.linkedin.com/developers/apps',
    credentialHelp: 'Producto Advertising API separado (rw_ads/r_ads).',
    products: [
      { key: 'advertising_api', label: 'Advertising API' },
      { key: 'rw_ads', label: 'rw_ads / r_ads' },
      { key: 'ad_accounts_granted', label: 'Cuentas publicitarias concedidas' },
    ],
    hasWebhook: false },
  { key: 'linkedin_pages_oauth', label: 'LinkedIn Páginas (Community Management API)', icon: KeyRound, brand: 'linkedin', group: 'linkedin',
    helpUrl: 'https://www.linkedin.com/developers/apps',
    credentialHelp: 'App nueva para Page Management. Webhook: https://app.cavernicolapp.com/api/webhooks/linkedin',
    products: [
      { key: 'community_management_api', label: 'Community Management API — Page Management (listar páginas administradas, publicar como página, leer/responder comentarios)' },
      { key: 'standard_tier', label: 'Standard Tier aprobado (marca informativa — la suscripción de webhook real solo se activa cuando la app la crea y LinkedIn la confirma, nunca solo por marcar esta casilla)' },
    ],
    needsDeveloperApplication: true,
    hasWebhook: false },
];

const OAUTH_GROUPS = [
  { key: 'google', title: 'Google OAuth', subtitle: 'Calendar, Sheets (futuro) y servicios Google — fondo amarillo/verde de la marca.' },
  { key: 'microsoft', title: 'Microsoft / Outlook OAuth', subtitle: 'Outlook y Microsoft 365 Calendar — azul Microsoft. Los tenants conectan su cuenta en Agenda.' },
  { key: 'meta', title: 'Meta / Facebook OAuth', subtitle: 'Facebook Login, páginas, Messenger y Meta Ads — azul Facebook.' },
  { key: 'instagram', title: 'Instagram OAuth', subtitle: 'Instagram Login API — rosa de la marca, separado de Facebook.' },
  { key: 'tiktok', title: 'TikTok OAuth', subtitle: 'Developers y Business — bloque negro de la marca.' },
  { key: 'linkedin', title: 'LinkedIn OAuth', subtitle: 'Login, Ads y Páginas — tarjeta blanca LinkedIn.' },
];

const secretLooksLikeGuid = (v) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test((v || '').trim());

export default function InfrastructureConfig() {

  const [config, setConfig] = useState({});
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState(null);
  const [searchResults, setSearchResults] = useState({});
  const [searching, setSearching] = useState(null);
  const [manualForm, setManualForm] = useState({
    provider_key: 'gupshup', identity_value: '', api_key: '', app_name: '', source_phone: '',
    gupshup_app_id: '',
  });
  const [gupshupApps, setGupshupApps] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [asignarA, setAsignarA] = useState({});
  const [poolOcupado, setPoolOcupado] = useState(null);
  const [oauthApps, setOauthApps] = useState({});
  const [oauthForms, setOauthForms] = useState({});
  const [savingOauth, setSavingOauth] = useState(null);
  const [turnstile, setTurnstile] = useState(null);
  const [turnstileForm, setTurnstileForm] = useState({
    site_key: '', secret_key: '', enabled: true, expected_hostnames: 'app.cavernicolapp.com',
  });
  const [savingTurnstile, setSavingTurnstile] = useState(false);
  const [storage, setStorage] = useState(null);
  const [storageForm, setStorageForm] = useState({
    account_id: '', access_key_id: '', secret_access_key: '', endpoint: '', bucket: '',
    region: 'auto', env_prefix: 'production', upload_ttl_seconds: 900, download_ttl_seconds: 300,
    max_upload_bytes_global: 104857600, cors_origins: 'https://app.cavernicolapp.com', enabled: false,
  });
  const [savingStorage, setSavingStorage] = useState(false);
  const [testingStorage, setTestingStorage] = useState(false);

  const fetchAll = async () => {
    try {
      const [infraRes, poolRes, oauthRes, tsRes, stRes, negRes, gsAppsRes] = await Promise.allSettled([
        api.get('/admin/infrastructure'),
        api.get('/admin/infrastructure/pool'),
        api.get('/admin/infrastructure/oauth-apps'),
        api.get('/admin/infrastructure/turnstile'),
        api.get('/admin/infrastructure/storage'),
        // Para poder elegir a QUÉ negocio se le entrega cada identidad.
        api.get('/admin/tenants'),
        api.get('/admin/infrastructure/pool/gupshup-apps'),
      ]);
      if (negRes.status === 'fulfilled') setNegocios(negRes.value.data || []);
      if (infraRes.status === 'fulfilled') setConfig(infraRes.value.data);
      else toast.error(formatApiError(infraRes.reason?.response?.data?.detail));
      if (poolRes.status === 'fulfilled') setPool(poolRes.value.data);
      if (gsAppsRes.status === 'fulfilled') setGupshupApps(gsAppsRes.value.data?.items || []);
      if (oauthRes.status === 'fulfilled') setOauthApps(oauthRes.value.data);
      if (tsRes.status === 'fulfilled') {
        const tsData = tsRes.value.data;
        setTurnstile(tsData);
        // Bugfix: when never configured, API returns enabled=false → checkbox
        // was loaded OFF and a first save stored enabled:false with the keys.
        // If already configured, respect the stored flag; otherwise default ON.
        const enabledDefault = tsData.configured
          ? Boolean(tsData.enabled)
          : true;
        setTurnstileForm(f => ({
          ...f,
          site_key: tsData.site_key || '',
          secret_key: '',
          enabled: enabledDefault,
          expected_hostnames: tsData.expected_hostnames || 'app.cavernicolapp.com',
        }));
      } else {
        // Non-fatal: page still works; Turnstile form stays editable
        console.warn('Turnstile config load failed', tsRes.reason);
      }
      if (stRes.status === 'fulfilled') {
        const st = stRes.value.data;
        setStorage(st);
        const c = st.config || {};
        setStorageForm(f => ({
          ...f,
          account_id: '',
          access_key_id: '',
          secret_access_key: '',
          endpoint: c.endpoint || '',
          bucket: c.bucket || '',
          region: c.region || 'auto',
          env_prefix: c.env_prefix || 'production',
          upload_ttl_seconds: c.upload_ttl_seconds ?? 900,
          download_ttl_seconds: c.download_ttl_seconds ?? 300,
          max_upload_bytes_global: c.max_upload_bytes_global ?? 104857600,
          cors_origins: Array.isArray(c.cors_origins) ? c.cors_origins.join(', ') : (c.cors_origins || ''),
          enabled: Boolean(c.enabled),
        }));
      } else {
        console.warn('R2 storage config load failed', stRes.reason);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleSaveTurnstile = async () => {
    setSavingTurnstile(true);
    try {
      const payload = {
        enabled: Boolean(turnstileForm.enabled),
        expected_hostnames: turnstileForm.expected_hostnames || 'app.cavernicolapp.com',
      };
      if ((turnstileForm.site_key || '').trim()) payload.site_key = turnstileForm.site_key.trim();
      if ((turnstileForm.secret_key || '').trim()) payload.secret_key = turnstileForm.secret_key.trim();
      const { data } = await api.put('/admin/infrastructure/turnstile', payload);
      setTurnstile(data);
      setTurnstileForm(f => ({ ...f, secret_key: '', site_key: data.site_key || f.site_key }));
      toast.success(data.enabled ? 'Turnstile guardado y activo en el login' : 'Turnstile guardado (desactivado)');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingTurnstile(false); }
  };

  const handleSaveStorage = async () => {
    setSavingStorage(true);
    try {
      const payload = {
        endpoint: (storageForm.endpoint || '').trim() || undefined,
        bucket: (storageForm.bucket || '').trim() || undefined,
        region: storageForm.region || 'auto',
        env_prefix: storageForm.env_prefix || 'production',
        upload_ttl_seconds: Number(storageForm.upload_ttl_seconds) || 900,
        download_ttl_seconds: Number(storageForm.download_ttl_seconds) || 300,
        max_upload_bytes_global: Number(storageForm.max_upload_bytes_global) || 104857600,
        cors_origins: (storageForm.cors_origins || '').split(',').map(s => s.trim()).filter(Boolean),
        enabled: Boolean(storageForm.enabled),
      };
      if ((storageForm.account_id || '').trim()) payload.account_id = storageForm.account_id.trim();
      if ((storageForm.access_key_id || '').trim()) payload.access_key_id = storageForm.access_key_id.trim();
      if ((storageForm.secret_access_key || '').trim()) payload.secret_access_key = storageForm.secret_access_key.trim();
      const { data } = await api.put('/admin/infrastructure/storage', payload);
      setStorage(data);
      setStorageForm(f => ({ ...f, access_key_id: '', secret_access_key: '', account_id: '' }));
      toast.success('Almacenamiento R2 guardado (flag de entorno independiente)');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingStorage(false); }
  };

  const handleTestStorage = async () => {
    setTestingStorage(true);
    try {
      const { data } = await api.post('/admin/infrastructure/storage/test');
      setStorage(data);
      if (data.test?.ok) toast.success('Prueba de conexión R2 OK');
      else toast.error(data.test?.error || 'Prueba de conexión falló');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTestingStorage(false); }
  };

  const updateOauthForm = (providerKey, field, value) => {
    setOauthForms(f => ({ ...f, [providerKey]: { ...(f[providerKey] || {}), [field]: value } }));
  };

  const handleSaveOauthApp = async (app) => {
    const form = oauthForms[app.key] || {};
    if (!form.client_id || !form.client_secret) { toast.error('Completa client_id y client_secret'); return; }
    if ((app.key === 'outlook_oauth' || app.key === 'google_oauth') && secretLooksLikeGuid(form.client_secret)) {
      toast.error('Eso es el Secret ID (GUID). En Azure copia el Valor/Value del secreto, no el Id. Si no lo ves, crea un secreto nuevo.');
      return;
    }
    setSavingOauth(app.key);
    try {
      // Bug found live-testing this exact round: /credentials REPLACES
      // config_json wholesale server-side — omitting it here would
      // silently wipe the "productos aprobados" checklist (and, for
      // meta_oauth, the webhook_verify_token) on every credentials edit.
      // Always round-trip the config already on file.
      const existingConfig = oauthApps[app.key]?.config || {};
      await api.put(`/admin/infrastructure/${app.key}/credentials`, {
        display_name: app.label,
        credentials: { client_id: form.client_id, client_secret: form.client_secret },
        config: existingConfig,
      });
      toast.success(`App de ${app.label} guardada`);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingOauth(null); }
  };

  const [devAppForms, setDevAppForms] = useState({});
  const [savingDevApp, setSavingDevApp] = useState(null);
  const handleSaveDeveloperApplication = async (app) => {
    const urn = (devAppForms[app.key] || '').trim();
    if (!urn) { toast.error('Ingresa el Developer Application URN'); return; }
    setSavingDevApp(app.key);
    try {
      const { data } = await api.put(`/admin/infrastructure/oauth-apps/${app.key}/developer-application`,
        { developer_application_urn: urn });
      setOauthApps(a => ({ ...a, [app.key]: { ...a[app.key], config: data.config } }));
      toast.success('Developer Application URN guardado');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingDevApp(null); }
  };

  const [savingProducts, setSavingProducts] = useState(null);
  const handleToggleProduct = async (app, productKey, checked) => {
    const current = oauthApps[app.key]?.config?.approved_products || {};
    const next = { ...current, [productKey]: checked };
    setSavingProducts(app.key);
    try {
      const { data } = await api.put(`/admin/infrastructure/oauth-apps/${app.key}/products`, { products: next });
      setOauthApps(a => ({ ...a, [app.key]: { ...a[app.key], config: data.config } }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingProducts(null); }
  };

  const copyRedirectUri = (uri) => {
    navigator.clipboard.writeText(uri);
    toast.success('Redirect URI copiado');
  };

  const copyValue = (value, label) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copiado`);
  };

  const [regenerating, setRegenerating] = useState(null);
  const handleRegenerateVerifyToken = async (app) => {
    if (!window.confirm(`Esto genera un token nuevo. Tendrás que volver a pegarlo en Meta y verificar de nuevo — la suscripción actual dejará de funcionar hasta hacerlo. ¿Continuar?`)) return;
    setRegenerating(app.key);
    try {
      await api.post(`/admin/infrastructure/oauth-apps/${app.key}/regenerate-verify-token`);
      toast.success('Token de verificación regenerado');
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRegenerating(null); }
  };

  useEffect(() => { fetchAll(); }, []);

  const updateForm = (providerKey, field, value) => {
    setForms(f => ({ ...f, [providerKey]: { ...(f[providerKey] || {}), [field]: value } }));
  };

  const handleSaveCredentials = async (provider) => {
    const form = forms[provider.key] || {};
    setSaving(provider.key);
    try {
      await api.put(`/admin/infrastructure/${provider.key}/credentials`, {
        display_name: provider.label,
        credentials: form,
      });
      toast.success(`Credenciales de ${provider.label} guardadas`);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const handleSearchNumbers = async (provider) => {
    setSearching(provider.key);
    try {
      const { data } = await api.post(`/admin/infrastructure/${provider.key}/available-numbers`, { country: 'MX' });
      setSearchResults(r => ({ ...r, [provider.key]: data.numbers }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSearching(null); }
  };

  const handlePurchase = async (provider, number) => {
    const estimate = provider.channel === 'voice' ? 'Costo estimado: ~$1-2 USD/mes (tarifa estándar de Twilio para número local, se confirma en la factura de Twilio)' : 'Costo estimado: ~$1-2 USD/mes';
    if (!window.confirm(`¿Comprar el número ${number.phone_number}?\n\n${estimate}\n\nEsto genera un cargo real y recurrente en la cuenta de Twilio de la plataforma.`)) return;
    try {
      await api.post(`/admin/infrastructure/${provider.key}/purchase-number`, {
        phone_number: number.phone_number, channel_type: provider.channel,
      });
      toast.success(`Número ${number.phone_number} comprado y agregado al pool`);
      setSearchResults(r => ({ ...r, [provider.key]: [] }));
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleManualAdd = async () => {
    if (!manualForm.identity_value.trim()) { toast.error('Ingresa el número/identidad aprobada'); return; }
    // Las credenciales de ESTE WABA. Sin ellas el negocio al que se le entregue
    // mandaría con las globales, o sea desde el número de otro.
    const cred = {};
    if (manualForm.provider_key === 'gupshup') {
      ['api_key', 'app_name', 'source_phone'].forEach((k) => {
        if ((manualForm[k] || '').trim()) cred[k] = manualForm[k].trim();
      });
    }
    try {
      await api.post('/admin/infrastructure/pool/manual', {
        provider_key: manualForm.provider_key, channel_type: 'whatsapp', identity_value: manualForm.identity_value,
        credenciales: Object.keys(cred).length ? cred : undefined,
        // Es la costura entre el inventario y el consumo: el inventario
        // identifica por número y las facturas llegan por app_id. Sin esto no
        // se sabe de qué negocio fue el gasto y no hay bolsa que descontar.
        metadata: (manualForm.gupshup_app_id || '').trim()
          ? { gupshup_app_id: manualForm.gupshup_app_id.trim() } : undefined,
      });
      toast.success('Número agregado al inventario');
      setManualForm({ provider_key: 'gupshup', identity_value: '', api_key: '', app_name: '', source_phone: '', gupshup_app_id: '' });
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const entregarIdentidad = async (entrada) => {
    const tid = (asignarA[entrada.id] || '').trim();
    if (!tid) { toast.error('Elige a qué negocio se lo entregas'); return; }
    if (!entrada.tiene_credenciales_propias) {
      // Se avisa ANTES, no cuando ya salió mal: sin credenciales propias el
      // envío cae a las globales y los mensajes salen del número de otro.
      if (!window.confirm(
        `«${entrada.identity_value}» no tiene credenciales propias guardadas.\n\n` +
        'Los mensajes de ese negocio saldrían con las credenciales globales, ' +
        'es decir desde OTRO número. ¿Entregarlo de todas formas?')) return;
    }
    setPoolOcupado(entrada.id);
    try {
      await api.post('/admin/infrastructure/assign', {
        tenant_id: tid, provider_key: entrada.provider_key,
        channel_type: entrada.channel_type, pool_id: entrada.id,
      });
      toast.success(`${entrada.identity_value} entregado`);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPoolOcupado(null); }
  };

  const liberarIdentidad = async (entrada) => {
    if (!entrada.assigned_integration_instance_id) {
      toast.error('Esa identidad no tiene una conexión activa que liberar');
      return;
    }
    if (!window.confirm(
      `¿Quitarle «${entrada.identity_value}» a ${entrada.assigned_tenant_name || 'ese negocio'}?\n\n` +
      'Dejará de poder mandar por ese número. El número queda apartado: ' +
      'no se le entrega a nadie más hasta que tú lo devuelvas al inventario.')) return;
    setPoolOcupado(entrada.id);
    try {
      await api.post(`/admin/infrastructure/release/${entrada.assigned_integration_instance_id}`);
      toast.success('Identidad liberada y apartada');
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPoolOcupado(null); }
  };

  const devolverAlInventario = async (entrada) => {
    const antes = (entrada.historial || []).map(h => h.tenant_name).filter(Boolean).join(', ');
    if (!window.confirm(
      `Devolver «${entrada.identity_value}» al inventario.\n\n` +
      (antes ? `Este número ya estuvo con: ${antes}.\n\n` : '') +
      'Quien le escriba de memoria caerá en la bandeja del siguiente negocio ' +
      'que lo reciba. ¿Continuar?')) return;
    setPoolOcupado(entrada.id);
    try {
      await api.post(`/admin/infrastructure/pool/${entrada.id}/devolver`, { confirmado: true });
      toast.success('Disponible para entregar de nuevo');
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPoolOcupado(null); }
  };

  if (loading) return <PantallaCargando />;

  const renderOauthCard = (app) => {
    const cfg = oauthApps[app.key] || {};
    const form = oauthForms[app.key] || {};
    const brand = BRAND[app.brand] || BRAND.email;
    const dark = brand.dark;
    return (
      <div key={app.key} className={`border rounded-xl p-5 overflow-hidden ${brand.card}`} data-testid={`infra-oauth-${app.key}`}>
        <div className={`h-1.5 -mt-5 -mx-5 mb-4 ${brand.bar}`} />
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <KeyRound className={`w-4 h-4 ${dark ? 'text-white' : brand.title}`} />
            <h3 className={`text-sm font-semibold ${brand.title}`}>{app.label}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${cfg.configured ? 'bg-emerald-50 text-emerald-600' : dark ? 'bg-zinc-800 text-zinc-300' : 'bg-white/80 text-[#94A3B8]'}`}>
              {cfg.configured ? 'Configurado' : 'Sin configurar'}
            </span>
          </div>
          <a href={app.helpUrl} target="_blank" rel="noreferrer" className={`text-xs hover:underline ${dark ? 'text-sky-300' : 'text-blue-500'}`}>Abrir consola →</a>
        </div>
        {cfg.redirect_uri && (
          <div className={`mb-3 p-2.5 border rounded-lg ${dark ? 'bg-zinc-900 border-zinc-700' : 'bg-white/70 border-white'}`}>
            <p className={`text-[10px] uppercase tracking-wide mb-1 ${dark ? 'text-zinc-400' : 'text-[#94A3B8]'}`}>Redirect URI autorizado</p>
            <div className="flex items-center gap-2">
              <code className={`text-xs break-all flex-1 ${dark ? 'text-zinc-100' : 'text-[#0F172A]'}`}>{cfg.redirect_uri}</code>
              <button onClick={() => copyRedirectUri(cfg.redirect_uri)} className={dark ? 'text-zinc-400' : 'text-[#64748B]'} title="Copiar">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        {app.credentialHelp && (
          <p className={`text-xs mb-3 rounded-lg p-2.5 ${dark ? 'bg-zinc-900 text-zinc-300 border border-zinc-700' : 'bg-white/60 text-[#64748B] border border-white'}`}>{app.credentialHelp}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className={`text-xs mb-1 block ${dark ? 'text-zinc-400' : 'text-[#64748B]'}`}>Client ID</label>
            <Input placeholder={cfg.masked_credentials?.client_id || ''} value={form.client_id || ''}
              onChange={e => updateOauthForm(app.key, 'client_id', e.target.value)} className="text-sm bg-white" />
          </div>
          <div>
            <label className={`text-xs mb-1 block ${dark ? 'text-zinc-400' : 'text-[#64748B]'}`}>Client Secret</label>
            <Input type="password" placeholder={cfg.masked_credentials?.client_secret || ''} value={form.client_secret || ''}
              onChange={e => updateOauthForm(app.key, 'client_secret', e.target.value)} className="text-sm bg-white" />
          </div>
        </div>
        <Button size="sm" onClick={() => handleSaveOauthApp(app)} disabled={savingOauth === app.key}>
          {savingOauth === app.key ? 'Guardando...' : 'Guardar app'}
        </Button>
        {app.products && (
          <div className={`mt-4 pt-4 border-t ${dark ? 'border-zinc-700' : 'border-black/5'}`}>
            <p className={`text-xs font-semibold mb-2 ${brand.title}`}>Productos solicitados/aprobados</p>
            <div className="space-y-1.5">
              {app.products.map(p => {
                const checked = !!cfg.config?.approved_products?.[p.key];
                return (
                  <label key={p.key} className={`flex items-center gap-2 text-xs cursor-pointer ${dark ? 'text-zinc-200' : 'text-[#0F172A]'}`}>
                    <input type="checkbox" checked={checked} disabled={savingProducts === app.key}
                      onChange={e => handleToggleProduct(app, p.key, e.target.checked)} />
                    {p.label}
                  </label>
                );
              })}
            </div>
          </div>
        )}
        {app.needsDeveloperApplication && (
          <div className={`mt-4 pt-4 border-t ${dark ? 'border-zinc-700' : 'border-black/5'}`}>
            <p className={`text-xs font-semibold mb-1 ${brand.title}`}>Developer Application URN</p>
            <p className={`text-xs mb-2 ${dark ? 'text-zinc-400' : 'text-[#64748B]'}`}>
              Requerido para webhooks en tiempo real (Standard Tier). Formato <code>urn:li:developerApplication:…</code>.
            </p>
            <div className="flex items-center gap-2">
              <Input placeholder={cfg.config?.developer_application_urn || 'urn:li:developerApplication:...'}
                value={devAppForms[app.key] ?? ''}
                onChange={e => setDevAppForms(f => ({ ...f, [app.key]: e.target.value }))}
                className="text-sm flex-1 bg-white" />
              <Button size="sm" disabled={savingDevApp === app.key} onClick={() => handleSaveDeveloperApplication(app)}>
                {savingDevApp === app.key ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
            {cfg.config?.developer_application_urn && (
              <p className={`text-xs mt-1.5 ${dark ? 'text-zinc-400' : 'text-[#64748B]'}`}>Actual: <code>{cfg.config.developer_application_urn}</code></p>
            )}
          </div>
        )}
        {app.hasWebhook && (
          <div className="mt-4 pt-4 border-t border-black/5">
            <p className={`text-xs font-semibold mb-1 ${brand.title}`}>Webhooks</p>
            <p className="text-xs text-[#64748B] mb-3">En Meta → Webhooks pega estos valores (no el Redirect URI de login).</p>
            {cfg.webhook_callback_url && (
              <div className="mb-2 p-2.5 bg-white/70 border rounded-lg">
                <p className="text-[10px] uppercase text-[#94A3B8] mb-1">URL de devolución de llamada</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs break-all flex-1">{cfg.webhook_callback_url}</code>
                  <button onClick={() => copyValue(cfg.webhook_callback_url, 'URL de devolución de llamada')}><Copy className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}
            {cfg.webhook_verify_token && (
              <div className="mb-2 p-2.5 bg-white/70 border rounded-lg">
                <p className="text-[10px] uppercase text-[#94A3B8] mb-1">Token de verificación</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs break-all flex-1">{cfg.webhook_verify_token}</code>
                  <button onClick={() => copyValue(cfg.webhook_verify_token, 'Token de verificación')}><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleRegenerateVerifyToken(app)} disabled={regenerating === app.key}>
                    <RefreshCw className={`w-3.5 h-3.5 ${regenerating === app.key ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="infrastructure-config-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2"><Server className="w-6 h-6" /> Infraestructura</h1>
        <p className="text-sm text-[#64748B] mt-1">Cuentas propias de CavernicolApp por marca. Los bloques de color son solo UX — las credenciales no cambian de API.</p>
      </div>

      <div className="space-y-6">
        {/* Cloudflare Turnstile FIRST — login captcha (easy to miss if buried under Twilio/OAuth) */}
        <div className={`border-2 rounded-xl p-5 ${BRAND.cloudflare.card}`} data-testid="infra-turnstile">
          <div className={`h-1.5 -mt-5 -mx-5 mb-4 rounded-t-xl ${BRAND.cloudflare.bar}`} />
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Shield className={`w-4 h-4 ${BRAND.cloudflare.title}`} />
              <h2 className={`text-base font-bold ${BRAND.cloudflare.title}`}>Cloudflare Turnstile (captcha del login)</h2>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${turnstile?.enabled && turnstile?.has_secret ? 'bg-emerald-50 text-emerald-600' : turnstile?.configured ? 'bg-amber-50 text-amber-700' : 'bg-white/80 text-[#94A3B8]'}`}>
                {turnstile?.enabled && turnstile?.has_secret ? 'Activo en login' : turnstile?.configured ? 'Guardado (off)' : 'Sin configurar'}
              </span>
            </div>
            <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noreferrer" className="text-xs text-orange-600 hover:underline font-medium">
              Abrir Cloudflare Turnstile →
            </a>
          </div>
          <p className="text-xs text-[#64748B] mb-3 rounded-lg p-2.5 bg-white/70 border border-orange-100">
            Pega aquí la <strong>Clave de sitio</strong> y la <strong>Clave secreta</strong> del widget Managed de Cloudflare
            (dominio <code className="text-[11px]">app.cavernicolapp.com</code>). Activa el checkbox y guarda para mostrar el captcha en el login.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <FieldHelp label="Clave de sitio (site key)" help={FIELD_HELP.turnstile_site} htmlFor="turnstile-site">
              <Input
                id="turnstile-site"
                placeholder={turnstile?.site_key || '0x4AAAA...'}
                value={turnstileForm.site_key}
                onChange={e => setTurnstileForm(f => ({ ...f, site_key: e.target.value }))}
                className="text-sm bg-white"
                autoComplete="off"
              />
            </FieldHelp>
            <FieldHelp label="Clave secreta (secret key)" help={FIELD_HELP.turnstile_secret} htmlFor="turnstile-secret">
              <Input
                id="turnstile-secret"
                type="password"
                placeholder={turnstile?.has_secret ? '•••••••• (dejar vacío para no cambiar)' : '0x4AAAA...'}
                value={turnstileForm.secret_key}
                onChange={e => setTurnstileForm(f => ({ ...f, secret_key: e.target.value }))}
                className="text-sm bg-white"
                autoComplete="new-password"
              />
            </FieldHelp>
            <FieldHelp label="Hostnames permitidos" help={FIELD_HELP.turnstile_hostnames} htmlFor="turnstile-hosts">
              <Input
                id="turnstile-hosts"
                value={turnstileForm.expected_hostnames}
                onChange={e => setTurnstileForm(f => ({ ...f, expected_hostnames: e.target.value }))}
                className="text-sm bg-white"
              />
            </FieldHelp>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={Boolean(turnstileForm.enabled)}
                  onChange={e => setTurnstileForm(f => ({ ...f, enabled: e.target.checked }))}
                  className="rounded border-orange-300"
                />
                Activar captcha en el login
              </label>
            </div>
          </div>
          <Button size="sm" onClick={handleSaveTurnstile} disabled={savingTurnstile} className="bg-orange-600 hover:bg-orange-700 text-white">
            {savingTurnstile ? 'Guardando...' : 'Guardar Turnstile'}
          </Button>
        </div>

        {/* R2-02 — Cloudflare R2 object storage (not wired to modules yet) */}
        <div className={`border-2 rounded-xl p-5 ${BRAND.cloudflare.card}`} data-testid="infra-storage-r2">
          <div className={`h-1.5 -mt-5 -mx-5 mb-4 rounded-t-xl ${BRAND.cloudflare.bar}`} />
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Server className={`w-4 h-4 ${BRAND.cloudflare.title}`} />
              <h2 className={`text-base font-bold ${BRAND.cloudflare.title}`}>Almacenamiento en la nube (Cloudflare R2)</h2>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                storage?.flags?.operational ? 'bg-emerald-50 text-emerald-600'
                  : storage?.configured ? 'bg-amber-50 text-amber-700'
                    : 'bg-white/80 text-[#94A3B8]'
              }`}>
                {storage?.flags?.operational ? 'Operacional' : storage?.configured ? (storage?.config?.enabled ? 'Configurado (kill switch off)' : 'Guardado (disabled)') : 'Sin configurar'}
              </span>
            </div>
            <a href="https://dash.cloudflare.com/?to=/:account/r2" target="_blank" rel="noreferrer" className="text-xs text-orange-600 hover:underline font-medium">
              Abrir Cloudflare R2 →
            </a>
          </div>
          <p className="text-xs text-[#64748B] mb-3 rounded-lg p-2.5 bg-white/70 border border-orange-100">
            Credenciales S3 globales de la plataforma (no por tenant). R2-02: núcleo listo, <strong>sin</strong> integración a Knowledge/Social/Ads.
            Kill switch de entorno: <code className="text-[11px]">STORAGE_R2_ENABLED</code> (default false). Una prueba exitosa <strong>no</strong> activa R2 sola.
          </p>
          <p className="text-[11px] text-[#64748B] mb-3">{storage?.cors_note}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <FieldHelp label="Account ID" help={FIELD_HELP.r2_account} htmlFor="r2-account">
              <Input id="r2-account" type="password" autoComplete="off"
                placeholder={storage?.masked?.account_id || storage?.config?.account_id_configured ? '•••• (vacío = no cambiar)' : '32 hex'}
                value={storageForm.account_id}
                onChange={e => setStorageForm(f => ({ ...f, account_id: e.target.value }))}
                className="text-sm bg-white" />
            </FieldHelp>
            <FieldHelp label="Endpoint S3" help={FIELD_HELP.r2_endpoint} htmlFor="r2-endpoint">
              <Input id="r2-endpoint" value={storageForm.endpoint}
                onChange={e => setStorageForm(f => ({ ...f, endpoint: e.target.value }))}
                placeholder="https://&lt;account&gt;.r2.cloudflarestorage.com"
                className="text-sm bg-white" />
            </FieldHelp>
            <FieldHelp label="Access Key ID" help={FIELD_HELP.r2_access} htmlFor="r2-access">
              <Input id="r2-access" type="password" autoComplete="new-password"
                placeholder={storage?.has_access_key ? '•••••••• (vacío = no cambiar)' : ''}
                value={storageForm.access_key_id}
                onChange={e => setStorageForm(f => ({ ...f, access_key_id: e.target.value }))}
                className="text-sm bg-white" />
            </FieldHelp>
            <FieldHelp label="Secret Access Key" help={FIELD_HELP.r2_secret} htmlFor="r2-secret">
              <Input id="r2-secret" type="password" autoComplete="new-password"
                placeholder={storage?.has_secret_access_key ? '•••••••• (vacío = no cambiar)' : ''}
                value={storageForm.secret_access_key}
                onChange={e => setStorageForm(f => ({ ...f, secret_access_key: e.target.value }))}
                className="text-sm bg-white" />
            </FieldHelp>
            <FieldHelp label="Bucket" help={FIELD_HELP.r2_bucket} htmlFor="r2-bucket">
              <Input id="r2-bucket" value={storageForm.bucket}
                onChange={e => setStorageForm(f => ({ ...f, bucket: e.target.value }))}
                className="text-sm bg-white" />
            </FieldHelp>
            <FieldHelp label="Prefijo de entorno" help="production / staging — prefijo de object keys" htmlFor="r2-env">
              <Input id="r2-env" value={storageForm.env_prefix}
                onChange={e => setStorageForm(f => ({ ...f, env_prefix: e.target.value }))}
                className="text-sm bg-white" />
            </FieldHelp>
            <FieldHelp label="Orígenes CORS esperados" help={FIELD_HELP.r2_cors} htmlFor="r2-cors">
              <Input id="r2-cors" value={storageForm.cors_origins}
                onChange={e => setStorageForm(f => ({ ...f, cors_origins: e.target.value }))}
                placeholder="https://app.cavernicolapp.com"
                className="text-sm bg-white" />
            </FieldHelp>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer select-none">
                <input type="checkbox" checked={Boolean(storageForm.enabled)}
                  onChange={e => setStorageForm(f => ({ ...f, enabled: e.target.checked }))}
                  className="rounded border-orange-300" />
                Habilitar R2 (plataforma)
                <HelpTooltip text={FIELD_HELP.r2_enabled} />
              </label>
            </div>
          </div>
          {storage?.last_test && (
            <p className={`text-xs mb-3 ${storage.last_test.ok ? 'text-emerald-700' : 'text-red-600'}`}>
              Última prueba: {storage.last_test.ok ? 'OK' : (storage.last_test.error || 'falló')}
              {storage.last_test.object_key ? ` · key ${storage.last_test.object_key}` : ''}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={handleSaveStorage} disabled={savingStorage} className="bg-orange-600 hover:bg-orange-700 text-white">
              {savingStorage ? 'Guardando...' : 'Guardar R2'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleTestStorage} disabled={testingStorage}
              className="border-orange-300 text-orange-800">
              {testingStorage ? 'Probando...' : 'Probar conexión'}
            </Button>
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-2">
            Env kill switch: {storage?.flags?.env_storage_r2_enabled ? 'ON' : 'OFF'} ·
            Plataforma enabled: {storage?.flags?.platform_enabled ? 'sí' : 'no'} ·
            Operacional: {storage?.flags?.operational ? 'sí' : 'no'}
          </p>
        </div>

        {PROVIDERS.map(provider => {
          const cfg = config[provider.key] || {};
          const form = forms[provider.key] || {};
          const Icon = provider.icon;
          const brand = BRAND[provider.brand] || BRAND.email;
          return (
            <div key={provider.key} className={`border rounded-xl p-5 ${brand.card}`} data-testid={`infra-provider-${provider.key}`}>
              <div className={`h-1.5 -mt-5 -mx-5 mb-4 rounded-t-xl ${brand.bar}`} />
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${brand.title}`} />
                  <h3 className={`text-sm font-semibold ${brand.title}`}>{provider.label}</h3>
                  {provider.autoSearch && <HelpTooltip text={TWILIO_FLOW_HELP} />}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${cfg.configured ? 'bg-emerald-50 text-emerald-600' : 'bg-white/80 text-[#94A3B8]'}`}>
                    {cfg.configured ? 'Configurado' : 'Sin configurar'}
                  </span>
                </div>
              </div>
              {provider.autoSearch && (
                <p className="text-xs text-[#64748B] mb-3">Paso 1: guarda la cuenta. Paso 2: busca números — no escribas el número en el formulario de credenciales.</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                {provider.fields.map(field => (
                  <FieldHelp key={field} label={field} help={FIELD_HELP[field]} htmlFor={`${provider.key}-${field}`}>
                    <Input
                      id={`${provider.key}-${field}`}
                      type={field.includes('password') || field.includes('token') || field.includes('key') ? 'password' : 'text'}
                      placeholder={cfg.masked_credentials?.[field] || ''}
                      value={form[field] || ''}
                      onChange={e => updateForm(provider.key, field, e.target.value)}
                      className="text-sm bg-white"
                    />
                  </FieldHelp>
                ))}
              </div>
              <Button size="sm" onClick={() => handleSaveCredentials(provider)} disabled={saving === provider.key}>
                {saving === provider.key ? 'Guardando...' : 'Guardar credenciales'}
              </Button>
              {provider.autoSearch && cfg.configured && (
                <div className="mt-4 pt-4 border-t border-black/5">
                  <Button size="sm" variant="outline" onClick={() => handleSearchNumbers(provider)} disabled={searching === provider.key} className="bg-white">
                    <Search className="w-3.5 h-3.5 mr-1.5" /> {searching === provider.key ? 'Buscando...' : 'Buscar números disponibles (MX)'}
                  </Button>
                  {(searchResults[provider.key] || []).length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {searchResults[provider.key].map(n => (
                        <div key={n.phone_number} className="flex items-center justify-between p-2 bg-white/80 rounded-lg text-xs">
                          <span className="font-medium">{n.phone_number}</span>
                          <span className="text-[#94A3B8]">{n.locality || n.region || ''}</span>
                          <Button size="sm" variant="outline" onClick={() => handlePurchase(provider, n)}>
                            <ShoppingCart className="w-3 h-3 mr-1" /> Comprar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {provider.showManualNumber && (
                <div className="mt-4 pt-4 border-t border-[#6B3FA0]/20" data-testid="gupshup-manual-number">
                  <h4 className={`text-sm font-semibold mb-1 ${brand.title}`}>Agregar número de Gupshup ya aprobado</h4>
                  <p className="text-xs text-[#64748B] mb-3">
                    La verificación de Meta no se automatiza — da de alta aquí el número aprobado en Gupshup.
                    {' '}<strong>Pon también las tres credenciales de ESA app.</strong> Cada WABA
                    tiene las suyas: si las dejas vacías, el negocio al que se lo entregues
                    mandará con las credenciales generales, es decir <strong>desde el número
                    de otro</strong>, y las respuestas caerán en la bandeja equivocada.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <Input placeholder="Número (ej. +5215500000000)" value={manualForm.identity_value}
                      onChange={e => setManualForm(f => ({ ...f, identity_value: e.target.value }))} className="text-sm bg-white" />
                    <Input placeholder="app_name (ej. BanusResidencial)" value={manualForm.app_name}
                      onChange={e => setManualForm(f => ({ ...f, app_name: e.target.value }))} className="text-sm bg-white" />
                    <Input placeholder="source_phone (solo dígitos)" value={manualForm.source_phone}
                      onChange={e => setManualForm(f => ({ ...f, source_phone: e.target.value }))} className="text-sm bg-white" />
                    <Input placeholder="api_key de esa app" type="password" value={manualForm.api_key}
                      onChange={e => setManualForm(f => ({ ...f, api_key: e.target.value }))} className="text-sm bg-white" />
                    {gupshupApps.length > 0 ? (
                      <select
                        className="text-sm bg-white border rounded-md h-9 px-2 w-full"
                        value={manualForm.gupshup_app_id}
                        onChange={e => {
                          const id = e.target.value;
                          const app = gupshupApps.find(a => a.gupshup_app_id === id);
                          setManualForm(f => ({
                            ...f,
                            gupshup_app_id: id,
                            app_name: f.app_name || app?.app_name || '',
                          }));
                        }}
                      >
                        <option value="">Elige App ID de Gupshup…</option>
                        {gupshupApps.map(a => (
                          <option key={a.gupshup_app_id} value={a.gupshup_app_id}>
                            {a.app_name || a.gupshup_app_id}
                            {a.en_inventario ? ' (ya en inventario)' : ''}
                            {' — '}{a.gupshup_app_id}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input placeholder="App ID de Gupshup (para el consumo)" value={manualForm.gupshup_app_id}
                        onChange={e => setManualForm(f => ({ ...f, gupshup_app_id: e.target.value }))} className="text-sm bg-white" />
                    )}
                  </div>
                  <p className="text-[11px] text-[#94A3B8] mb-2">
                    El <strong>App ID</strong> es lo que ata el gasto a este negocio.
                    Se valida contra la lista real de Gupshup Partner: si lo tecleas mal,
                    el alta se rechaza (no se guarda gasto sin dueño).
                  </p>
                  <Button size="sm" onClick={handleManualAdd}><Plus className="w-3.5 h-3.5 mr-1" /> Agregar al inventario</Button>
                </div>
              )}
            </div>
          );
        })}

        <RemitenteAvisosSection />
        <SesInfraSection />

        <div className="border-t border-[#E2E8F0] pt-4">
          <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5" /> Apps OAuth por marca</h2>
          <p className="text-sm text-[#64748B] mb-4">Google, Meta, Instagram, TikTok y LinkedIn en bloques separados con los colores de cada marca.</p>
        </div>

        {OAUTH_GROUPS.map(group => {
          const apps = OAUTH_APPS.filter(a => a.group === group.key);
          if (!apps.length) return null;
          return (
            <div key={group.key} className="space-y-3" data-testid={`oauth-group-${group.key}`}>
              <div>
                <h3 className="text-sm font-bold text-[#0F172A]">{group.title}</h3>
                <p className="text-xs text-[#94A3B8]">{group.subtitle}</p>
              </div>
              <div className="space-y-3">
                {apps.map(renderOauthCard)}
              </div>
            </div>
          );
        })}

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="pool-de-numeros">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-1">Inventario de números y WABAs</h3>
          <p className="text-xs text-[#64748B] mb-3">
            Los números de la plataforma que le prestas a tus negocios. Un número
            entregado sigue siendo tuyo: cuando el negocio se va, se lo quitas y
            lo vuelves a usar con otro.
            {' '}<strong>Un número apartado no se entrega solo</strong> — tienes que
            devolverlo al inventario a mano, porque quien le escriba de memoria
            caerá en la bandeja del siguiente que lo reciba.
          </p>
          {pool.length === 0 ? (
            <p className="text-sm text-[#64748B] text-center py-4">Sin números en el inventario todavía</p>
          ) : (
            <table className="w-full text-sm" data-testid="pool-tabla">
              <thead><tr className="text-left text-xs text-[#94A3B8] border-b border-[#F1F5F9]">
                <th className="pb-2">Identidad</th><th className="pb-2">Canal</th><th className="pb-2">Estado</th>
                <th className="pb-2">Negocio</th><th className="pb-2">Origen</th><th className="pb-2"></th>
              </tr></thead>
              <tbody>
                {pool.map(e => (
                  <tr key={e.id} className="border-b border-[#F8FAFC] align-top">
                    <td className="py-2 text-[#0F172A]">
                      {e.identity_value}
                      {!e.tiene_credenciales_propias && e.provider_key === 'gupshup' && (
                        <span className="block text-[10px] text-amber-600">
                          sin credenciales propias — saldría desde otro número
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-[#64748B]">{e.channel_type}</td>
                    <td className="py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${e.status === 'available' ? 'bg-emerald-50 text-emerald-600' : e.status === 'assigned' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-700'}`}>
                        {e.status === 'available' ? 'Disponible' : e.status === 'assigned' ? 'Entregado' : 'Apartado'}
                      </span>
                    </td>
                    <td className="py-2 text-[#0F172A] text-xs">
                      {e.assigned_tenant_name || <span className="text-[#94A3B8]">—</span>}
                      {(e.historial || []).length > 0 && (
                        <span className="block text-[10px] text-[#94A3B8]">
                          antes: {(e.historial || []).map(h => h.tenant_name || 'un negocio dado de baja').join(', ')}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-[#94A3B8] text-xs">{e.provisioned_via === 'twilio_api_auto' ? 'Compra automática' : 'Alta manual'}</td>
                    <td className="py-2 text-right">
                      {e.status === 'available' && (
                        <div className="flex gap-1 justify-end">
                          <select
                            className="text-xs border border-[#E2E8F0] rounded px-1 py-1 max-w-[150px]"
                            value={asignarA[e.id] || ''}
                            onChange={ev => setAsignarA(a => ({ ...a, [e.id]: ev.target.value }))}
                          >
                            <option value="">Elige negocio…</option>
                            {negocios.map(n => (
                              <option key={n.id} value={n.id}>{n.name}</option>
                            ))}
                          </select>
                          <Button size="sm" className="h-7 text-xs" disabled={poolOcupado === e.id}
                                  onClick={() => entregarIdentidad(e)}>
                            Entregar
                          </Button>
                        </div>
                      )}
                      {e.status === 'assigned' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={poolOcupado === e.id}
                                onClick={() => liberarIdentidad(e)}>
                          Quitar
                        </Button>
                      )}
                      {e.status === 'released' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={poolOcupado === e.id}
                                onClick={() => devolverAlInventario(e)}>
                          Devolver al inventario
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
